import {
  COLOR_GRAPH_DARK_RED,
  COLOR_GRAPH_DARK_BLUE,
  COLOR_DRYING_GRAPH_TEMP_ROOM_SENSOR,
  COLOR_DRYING_GRAPH_TEMP_FLOOR_SENSOR_1,
  COLOR_DRYING_GRAPH_TEMP_FLOOR_SENSOR_2,
  COLOR_DRYING_GRAPH_TEMP_FLOOR_SENSOR_3,
  COLOR_DRYING_GRAPH_TEMP_FLOOR_SENSOR_4,
  COLOR_DRYING_GRAPH_TEMP_FLOOR_SENSOR_5,
  COLOR_DRYING_GRAPH_TEMP_FLOOR_SENSOR_6,
  COLOR_DRYING_GRAPH_TEMP_FLOOR_SENSOR_7,
  COLOR_DRYING_GRAPH_TEMP_FLOOR_SENSOR_8,
  COLOR_DRYING_GRAPH_TEMP_DEFAULT,
  COLOR_DRYING_GRAPH_HUMIDITY_ROOM_SENSOR,
  COLOR_DRYING_GRAPH_HUMIDITY_FLOOR_SENSOR_1,
  COLOR_DRYING_GRAPH_HUMIDITY_FLOOR_SENSOR_2,
  COLOR_DRYING_GRAPH_HUMIDITY_FLOOR_SENSOR_3,
  COLOR_DRYING_GRAPH_HUMIDITY_FLOOR_SENSOR_4,
  COLOR_DRYING_GRAPH_HUMIDITY_FLOOR_SENSOR_5,
  COLOR_DRYING_GRAPH_HUMIDITY_FLOOR_SENSOR_6,
  COLOR_DRYING_GRAPH_HUMIDITY_FLOOR_SENSOR_7,
  COLOR_DRYING_GRAPH_HUMIDITY_FLOOR_SENSOR_8,
  COLOR_DRYING_GRAPH_HUMIDITY_DEFAULT,
  COLOR_DRYING_GRAPH_RESISTANCE_ROOM_SENSOR,
  COLOR_DRYING_GRAPH_RESISTANCE_FLOOR_SENSOR_1,
  COLOR_DRYING_GRAPH_RESISTANCE_FLOOR_SENSOR_2,
  COLOR_DRYING_GRAPH_RESISTANCE_FLOOR_SENSOR_3,
  COLOR_DRYING_GRAPH_RESISTANCE_FLOOR_SENSOR_4,
  COLOR_DRYING_GRAPH_RESISTANCE_FLOOR_SENSOR_5,
  COLOR_DRYING_GRAPH_RESISTANCE_FLOOR_SENSOR_6,
  COLOR_DRYING_GRAPH_RESISTANCE_FLOOR_SENSOR_7,
  COLOR_DRYING_GRAPH_RESISTANCE_FLOOR_SENSOR_8,
  COLOR_DRYING_GRAPH_RESISTANCE_DEFAULT,
  COLOR_DRYING_GRAPH_TEMP_PANEL_GROUND,
  COLOR_DRYING_GRAPH_TEMP_PANEL_WALL,
  COLOR_DRYING_GRAPH_INTENSITY,
} from "@/utils/colors";
import { chartSeriesColor } from "@/utils/Theme";
import { calculateSlotNumberFromIndex } from "./ComBox";

export interface IresSensorAndValueCombination {
  sensor: IresDeviceSensor;
  value: IresDeviceSensorValue;
}

const FACTOR = 1000;

export function iresSensorAndValueToKey(
  combination: IresSensorAndValueCombination
) {
  return combination.sensor * FACTOR + combination.value;
}

export function iresSensorAndValueFromKey(
  key: number
): IresSensorAndValueCombination {
  const valuePart = key % FACTOR;
  const rest = key - valuePart;

  return {
    sensor: rest / FACTOR,
    value: valuePart,
  };
}

export enum IresDeviceSensor {
  COM_BOX_ROOM_SENSOR,
  COM_BOX_GUIDE_SENSOR,

  COM_BOX_FLOOR_SENSOR_1,
  COM_BOX_FLOOR_SENSOR_2,
  COM_BOX_FLOOR_SENSOR_3,
  COM_BOX_FLOOR_SENSOR_4,

  COM_BOX_FLOOR_SENSOR_5,
  COM_BOX_FLOOR_SENSOR_6,
  COM_BOX_FLOOR_SENSOR_7,
  COM_BOX_FLOOR_SENSOR_8,

  PANEL_WALL,
  PANEL_GROUND,
}

export enum IresDeviceSensorValue {
  TEMPERATURE,
  RELATIVE_HUMIDITY,
  RESISTANCE_DIGITS,
  INTENSITY,
}

// ==================== FIRMWARE v4.0.0+ CHANGES ====================
export function getComBoxSensorForSensorIndex(
  sensorIndex: number
): IresDeviceSensor {
  const sensorSlot = sensorIndex === -1
    ? -1
    : calculateSlotNumberFromIndex(sensorIndex);

  switch (sensorSlot) {
    case 1:
      return IresDeviceSensor.COM_BOX_FLOOR_SENSOR_1;
    case 2:
      return IresDeviceSensor.COM_BOX_FLOOR_SENSOR_2;
    case 3:
      return IresDeviceSensor.COM_BOX_FLOOR_SENSOR_3;
    case 4:
      return IresDeviceSensor.COM_BOX_FLOOR_SENSOR_4;
    case 5:
      return IresDeviceSensor.COM_BOX_FLOOR_SENSOR_5;
    case 6:
      return IresDeviceSensor.COM_BOX_FLOOR_SENSOR_6;
    case 7:
      return IresDeviceSensor.COM_BOX_FLOOR_SENSOR_7;
    case 8:
      return IresDeviceSensor.COM_BOX_FLOOR_SENSOR_8;
    case -1:
      return IresDeviceSensor.COM_BOX_GUIDE_SENSOR;
    default:
      return IresDeviceSensor.COM_BOX_ROOM_SENSOR;
  }
}

/**
 * The un-themed base sensor series color (before {@link chartSeriesColor}).
 *
 * Exported so the graph extractor can stash it on each dataset (`iresBaseColor`)
 * and re-derive the themed `borderColor` on a live theme toggle without a full
 * re-extraction. Callers that need the display color should use
 * {@link getIresDeviceSensorColor} instead.
 */
export function getIresDeviceSensorBaseColor(
  iresDeviceSensor: IresDeviceSensor,
  iresDeviceSensorValue: IresDeviceSensorValue
) {
  switch (iresDeviceSensorValue) {
    case IresDeviceSensorValue.TEMPERATURE:
      switch (iresDeviceSensor) {
        case IresDeviceSensor.PANEL_GROUND:
          return COLOR_DRYING_GRAPH_TEMP_PANEL_GROUND;
        case IresDeviceSensor.PANEL_WALL:
          return COLOR_DRYING_GRAPH_TEMP_PANEL_WALL;

        case IresDeviceSensor.COM_BOX_GUIDE_SENSOR:
          return COLOR_GRAPH_DARK_RED;
        case IresDeviceSensor.COM_BOX_ROOM_SENSOR:
          return COLOR_DRYING_GRAPH_TEMP_ROOM_SENSOR;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_1:
          return COLOR_DRYING_GRAPH_TEMP_FLOOR_SENSOR_1;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_2:
          return COLOR_DRYING_GRAPH_TEMP_FLOOR_SENSOR_2;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_3:
          return COLOR_DRYING_GRAPH_TEMP_FLOOR_SENSOR_3;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_4:
          return COLOR_DRYING_GRAPH_TEMP_FLOOR_SENSOR_4;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_5:
          return COLOR_DRYING_GRAPH_TEMP_FLOOR_SENSOR_5;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_6:
          return COLOR_DRYING_GRAPH_TEMP_FLOOR_SENSOR_6;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_7:
          return COLOR_DRYING_GRAPH_TEMP_FLOOR_SENSOR_7;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_8:
          return COLOR_DRYING_GRAPH_TEMP_FLOOR_SENSOR_8;
        default:
          return COLOR_DRYING_GRAPH_TEMP_DEFAULT;
      }
    case IresDeviceSensorValue.RELATIVE_HUMIDITY:
      switch (iresDeviceSensor) {
        case IresDeviceSensor.COM_BOX_GUIDE_SENSOR:
          return COLOR_GRAPH_DARK_BLUE;
        case IresDeviceSensor.COM_BOX_ROOM_SENSOR:
          return COLOR_DRYING_GRAPH_HUMIDITY_ROOM_SENSOR;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_1:
          return COLOR_DRYING_GRAPH_HUMIDITY_FLOOR_SENSOR_1;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_2:
          return COLOR_DRYING_GRAPH_HUMIDITY_FLOOR_SENSOR_2;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_3:
          return COLOR_DRYING_GRAPH_HUMIDITY_FLOOR_SENSOR_3;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_4:
          return COLOR_DRYING_GRAPH_HUMIDITY_FLOOR_SENSOR_4;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_5:
          return COLOR_DRYING_GRAPH_HUMIDITY_FLOOR_SENSOR_5;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_6:
          return COLOR_DRYING_GRAPH_HUMIDITY_FLOOR_SENSOR_6;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_7:
          return COLOR_DRYING_GRAPH_HUMIDITY_FLOOR_SENSOR_7;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_8:
          return COLOR_DRYING_GRAPH_HUMIDITY_FLOOR_SENSOR_8;
        default:
          return COLOR_DRYING_GRAPH_HUMIDITY_DEFAULT;
      }
    case IresDeviceSensorValue.RESISTANCE_DIGITS:
      switch (iresDeviceSensor) {
        case IresDeviceSensor.COM_BOX_GUIDE_SENSOR:
          return COLOR_DRYING_GRAPH_RESISTANCE_ROOM_SENSOR;
        case IresDeviceSensor.COM_BOX_ROOM_SENSOR:
          return COLOR_DRYING_GRAPH_RESISTANCE_ROOM_SENSOR;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_1:
          return COLOR_DRYING_GRAPH_RESISTANCE_FLOOR_SENSOR_1;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_2:
          return COLOR_DRYING_GRAPH_RESISTANCE_FLOOR_SENSOR_2;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_3:
          return COLOR_DRYING_GRAPH_RESISTANCE_FLOOR_SENSOR_3;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_4:
          return COLOR_DRYING_GRAPH_RESISTANCE_FLOOR_SENSOR_4;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_5:
          return COLOR_DRYING_GRAPH_RESISTANCE_FLOOR_SENSOR_5;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_6:
          return COLOR_DRYING_GRAPH_RESISTANCE_FLOOR_SENSOR_6;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_7:
          return COLOR_DRYING_GRAPH_RESISTANCE_FLOOR_SENSOR_7;
        case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_8:
          return COLOR_DRYING_GRAPH_RESISTANCE_FLOOR_SENSOR_8;
        default:
          return COLOR_DRYING_GRAPH_RESISTANCE_DEFAULT;
      }
    case IresDeviceSensorValue.INTENSITY:
      return COLOR_DRYING_GRAPH_INTENSITY;
  }
}

/**
 * Sensor series color for the drying graph and its legend.
 *
 * The base palette is designed for the light plot background; in dark mode the
 * color is passed through {@link chartSeriesColor}, which lifts dark-toned hues
 * to stay readable on the dark plot backdrop. Both the graph datasets and the
 * legend call this same function, keeping their color strings identical (the
 * legend-hover highlight matching depends on that).
 */
export function getIresDeviceSensorColor(
  iresDeviceSensor: IresDeviceSensor,
  iresDeviceSensorValue: IresDeviceSensorValue
) {
  return chartSeriesColor(
    getIresDeviceSensorBaseColor(iresDeviceSensor, iresDeviceSensorValue)
  );
}

export function getIresDeviceSensorValueDescription(
  vue: Vue,
  iresDeviceSensorValue: IresDeviceSensorValue
): string {
  switch (iresDeviceSensorValue) {
    case IresDeviceSensorValue.TEMPERATURE:
      return vue.$t("iresDeviceSensor.temperature").toString();
    case IresDeviceSensorValue.RELATIVE_HUMIDITY:
      return vue.$t("iresDeviceSensor.humidity").toString();
    case IresDeviceSensorValue.RESISTANCE_DIGITS:
      return vue.$t("iresDeviceSensor.resistanceDigits").toString();
    case IresDeviceSensorValue.INTENSITY:
      return vue.$t("iresDeviceSensor.intensity").toString();
  }
}

// ==================== FIRMWARE v4.0.0+ CHANGES ====================
export function getIresDeviceSensorDescription(
  vue: Vue,
  iresDeviceSensor: IresDeviceSensor
): string {
  switch (iresDeviceSensor) {
    case IresDeviceSensor.COM_BOX_ROOM_SENSOR:
      return vue.$t("iresDeviceSensor.roomSensor").toString();
    case IresDeviceSensor.COM_BOX_GUIDE_SENSOR:
      return vue.$t("iresDeviceSensor.guideSensor").toString();
    case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_1:
      return vue.$t("iresDeviceSensor.floorSensor1").toString();
    case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_2:
      return vue.$t("iresDeviceSensor.floorSensor2").toString();
    case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_3:
      return vue.$t("iresDeviceSensor.floorSensor3").toString();
    case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_4:
      return vue.$t("iresDeviceSensor.floorSensor4").toString();
    case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_5:
      return vue.$t("iresDeviceSensor.floorSensor5").toString();
    case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_6:
      return vue.$t("iresDeviceSensor.floorSensor6").toString();
    case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_7:
      return vue.$t("iresDeviceSensor.floorSensor7").toString();
    case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_8:
      return vue.$t("iresDeviceSensor.floorSensor8").toString();
    case IresDeviceSensor.PANEL_WALL:
      return vue.$t("iresDeviceSensor.wall").toString();
    case IresDeviceSensor.PANEL_GROUND:
      return vue.$t("iresDeviceSensor.ground").toString();
  }
}

/** Maps an IresDeviceSensor enum value back to its sensor array index (0–8). */
export function getSensorIndexForIresDeviceSensor(
  sensor: IresDeviceSensor
): number | null {
  switch (sensor) {
    case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_1: return 0;
    case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_2: return 1;
    case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_3: return 2;
    case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_4: return 3;
    case IresDeviceSensor.COM_BOX_ROOM_SENSOR: return 4;
    case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_5: return 5;
    case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_6: return 6;
    case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_7: return 7;
    case IresDeviceSensor.COM_BOX_FLOOR_SENSOR_8: return 8;
    default: return null;
  }
}
